
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Input,
  Label,
  Button,
  Spinner,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const SyncLogs = () => {
  document.title = "HMS | Channel Manager Sync Logs";

  /* =========================================
     STATE
  ========================================= */

  const [dashboard, setDashboard] = useState({
    totalSyncs: 0,
    success: 0,
    failed: 0,
    pending: 0,
    retrying: 0,
    processing: 0,
  });

  const [otaLogs, setOtaLogs] = useState([]);

  const [channels, setChannels] = useState([]);

  const [loading, setLoading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const [filters, setFilters] = useState({
    channel: "",
    syncType: "",
    status: "",
  });

  /* =========================================
     STATUS HELPERS
  ========================================= */

  const normalizeStatus = (status) => {
    if (!status) return "UNKNOWN";

    return String(status).toUpperCase();
  };

  const getStatusLabel = (status) => {
    const value = normalizeStatus(status);

    const labels = {
      SUCCESS: "Success",
      FAILED: "Failed",
      PENDING: "Pending",
      PROCESSING: "Processing",
      RETRYING: "Retrying",
      UNKNOWN: "Unknown",
    };

    return labels[value] || value;
  };

  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case "SUCCESS":
        return "success";

      case "FAILED":
        return "danger";

      case "PENDING":
        return "warning";

      case "PROCESSING":
        return "info";

      case "RETRYING":
        return "secondary";

      default:
        return "light";
    }
  };

  /* =========================================
     LOAD CHANNELS
  ========================================= */

  const loadChannels = useCallback(async () => {
    try {
      const res = await api.get("/channels");

      setChannels(
        Array.isArray(res.data?.connections)
          ? res.data.connections
          : []
      );
    } catch (err) {
      console.error("Load Channels Error:", err);
    }
  }, []);

  /* =========================================
     LOAD DASHBOARD
  ========================================= */

  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get(
        "/sync-logs/dashboard"
      );

      setDashboard({
        totalSyncs:
          res.data?.dashboard?.totalSyncs || 0,

        success:
          res.data?.dashboard?.success || 0,

        failed:
          res.data?.dashboard?.failed || 0,

        pending:
          res.data?.dashboard?.pending || 0,

        retrying:
          res.data?.dashboard?.retrying || 0,

        processing:
          res.data?.dashboard?.processing || 0,
      });
    } catch (err) {
      console.error(
        "Load Sync Dashboard Error:",
        err
      );
    }
  }, []);

  /* =========================================
     LOAD LOGS
  ========================================= */

  const loadLogs = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setLoading(true);
        }

        const params = {};

        if (filters.channel) {
          params.channelManager =
            filters.channel;
        }

        if (filters.syncType) {
          params.syncType =
            filters.syncType;
        }

        if (filters.status) {
          params.status =
            filters.status;
        }

        const res = await api.get(
          "/sync-logs",
          {
            params,
          }
        );

        setOtaLogs(
          Array.isArray(res.data?.logs)
            ? res.data.logs
            : []
        );
      } catch (err) {
        console.error(
          "Load Sync Logs Error:",
          err
        );
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [filters]
  );

  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {
    loadDashboard();
    loadChannels();
    loadLogs();
  }, [
    loadDashboard,
    loadChannels,
    loadLogs,
  ]);

  /* =========================================
     REFRESH
  ========================================= */

  const handleRefresh = async () => {
    try {
      setRefreshing(true);

      await Promise.all([
        loadDashboard(),
        loadChannels(),
        loadLogs(false),
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  /* =========================================
     CLEAR FILTERS
  ========================================= */

  const clearFilters = async () => {
    setFilters({
      channel: "",
      syncType: "",
      status: "",
    });
  };

  /* =========================================
     WIDGETS
  ========================================= */

  const widgets = [
    {
      label: "Total Syncs",
      value: dashboard.totalSyncs,
      icon: "bx bx-transfer",
      color: "#0d6efd",
      bgColor: "rgba(13,110,253,.12)",
    },

    {
      label: "Successful",
      value: dashboard.success,
      icon: "bx bx-check-circle",
      color: "#198754",
      bgColor: "rgba(25,135,84,.12)",
    },

    {
      label: "Failed",
      value: dashboard.failed,
      icon: "bx bx-error-circle",
      color: "#dc3545",
      bgColor: "rgba(220,53,69,.12)",
    },

    {
      label: "Pending",
      value: dashboard.pending,
      icon: "bx bx-time-five",
      color: "#ffc107",
      bgColor: "rgba(255,193,7,.15)",
    },
  ];

  /* =========================================
     TABLE COLUMNS
  ========================================= */

  const syncColumns = useMemo(
    () => [
      {
        header: "Channel",
        accessorKey:
          "channelManager",

        cell: ({ getValue }) => {
          const value = getValue();

          return (
            <Badge color="primary">
              {value || "-"}
            </Badge>
          );
        },
      },

      {
        header: "Sync Type",
        accessorKey: "syncType",

        cell: ({ getValue }) => (
          <span className="fw-semibold">
            {getValue() || "-"}
          </span>
        ),
      },

      {
        header: "Room Type",
        accessorKey:
          "roomTypeName",

        cell: ({ getValue }) =>
          getValue() || "-",
      },

      {
        header: "Date",
        accessorKey: "date",

        cell: ({ row }) => {
          const value =
            row.original.date;

          if (!value) return "-";

          return new Date(
            value
          ).toLocaleDateString();
        },
      },

      {
        header: "Status",
        accessorKey: "status",

        cell: ({ getValue }) => {
          const status =
            normalizeStatus(
              getValue()
            );

          return (
            <Badge
              color={getStatusColor(
                status
              )}
            >
              {getStatusLabel(
                status
              )}
            </Badge>
          );
        },
      },

      {
        header: "Attempts",
        accessorFn: (row) =>
          row.retryCount ??
          row.attempt ??
          0,

        cell: ({ getValue }) =>
          getValue() || 0,
      },

      {
        header: "Response Time",
        accessorKey:
          "responseTime",

        cell: ({ getValue }) => {
          const value =
            Number(
              getValue() || 0
            );

          return `${value} ms`;
        },
      },

      {
        header: "Synced At",
        accessorKey:
          "syncedAt",

        cell: ({ getValue }) => {
          const value =
            getValue();

          if (!value) return "-";

          const date =
            new Date(value);

          if (
            Number.isNaN(
              date.getTime()
            )
          ) {
            return "-";
          }

          return date.toLocaleString();
        },
      },

      {
        header: "Error",
        accessorKey:
          "errorMessage",

        cell: ({ getValue }) => {
          const value =
            getValue();

          if (!value) {
            return (
              <span className="text-muted">
                -
              </span>
            );
          }

          return (
            <span
              className="text-danger"
              title={value}
              style={{
                display:
                  "inline-block",
                maxWidth:
                  "220px",
                overflow:
                  "hidden",
                textOverflow:
                  "ellipsis",
                whiteSpace:
                  "nowrap",
              }}
            >
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  /* =========================================
     CHANNEL HEALTH
  ========================================= */

  const getChannelStatus = (
    channel
  ) => {
    const status =
      normalizeStatus(
        channel.status
      );

    if (
      status === "CONNECTED" ||
      status === "SUCCESS"
    ) {
      return {
        label: "Connected",
        color: "success",
        width: "100%",
      };
    }

    if (
      status === "PROCESSING" ||
      status === "PENDING"
    ) {
      return {
        label: "Syncing",
        color: "warning",
        width: "70%",
      };
    }

    return {
      label: "Disconnected",
      color: "danger",
      width: "25%",
    };
  };

  /* =========================================
     RENDER
  ========================================= */

  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb
          title="Channel Manager"
          pageTitle="Sync Logs"
        />

        {/* =====================================
            DASHBOARD
        ===================================== */}

        <Row>
          {widgets.map(
            (item, index) => (
              <Col
                xl={3}
                md={6}
                key={index}
              >
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">

                    <div>
                      <p className="text-uppercase text-muted fw-semibold mb-1">
                        {item.label}
                      </p>

                      <h3 className="mb-0">
                        <CountUp
                          start={0}
                          end={item.value}
                          duration={1.5}
                        />
                      </h3>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 10,
                        backgroundColor:
                          item.bgColor,
                      }}
                    >
                      <i
                        className={
                          item.icon
                        }
                        style={{
                          fontSize: 24,
                          color:
                            item.color,
                        }}
                      />
                    </div>

                  </CardBody>
                </Card>
              </Col>
            )
          )}
        </Row>

        {/* =====================================
            EXTRA STATUS
        ===================================== */}

        <Row>
          <Col xl={6}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">

                  <div>
                    <p className="text-muted mb-1">
                      Processing
                    </p>

                    <h5 className="mb-0">
                      {dashboard.processing}
                    </h5>
                  </div>

                  <Badge color="info">
                    PROCESSING
                  </Badge>

                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={6}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">

                  <div>
                    <p className="text-muted mb-1">
                      Retrying
                    </p>

                    <h5 className="mb-0">
                      {dashboard.retrying}
                    </h5>
                  </div>

                  <Badge color="secondary">
                    RETRYING
                  </Badge>

                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* =====================================
            FILTERS
        ===================================== */}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">

            <h4 className="card-title mb-0">
              Sync Filters
            </h4>

            <Button
              color="light"
              size="sm"
              onClick={
                handleRefresh
              }
              disabled={
                refreshing
              }
            >
              {refreshing ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-1"
                  />
                  Refreshing
                </>
              ) : (
                <>
                  <i className="bx bx-refresh me-1" />
                  Refresh
                </>
              )}
            </Button>

          </CardHeader>

          <CardBody>
            <Row>

              <Col md={3}>
                <Label>
                  Channel
                </Label>

                <Input
                  type="select"
                  value={
                    filters.channel
                  }
                  onChange={(e) =>
                    setFilters(
                      (prev) => ({
                        ...prev,
                        channel:
                          e.target
                            .value,
                      })
                    )
                  }
                >
                  <option value="">
                    All Channels
                  </option>

                  {channels.map(
                    (channel) => (
                      <option
                        key={
                          channel._id
                        }
                        value={
                          channel.channel
                        }
                      >
                        {channel.channel}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col md={3}>
                <Label>
                  Sync Type
                </Label>

                <Input
                  type="select"
                  value={
                    filters.syncType
                  }
                  onChange={(e) =>
                    setFilters(
                      (prev) => ({
                        ...prev,
                        syncType:
                          e.target
                            .value,
                      })
                    )
                  }
                >
                  <option value="">
                    All
                  </option>

                  <option value="Inventory">
                    Inventory
                  </option>

                  <option value="Rates">
                    Rates
                  </option>

                  <option value="Restrictions">
                    Restrictions
                  </option>

                  <option value="Reservations">
                    Reservations
                  </option>
                </Input>
              </Col>

              <Col md={3}>
                <Label>
                  Status
                </Label>

                <Input
                  type="select"
                  value={
                    filters.status
                  }
                  onChange={(e) =>
                    setFilters(
                      (prev) => ({
                        ...prev,
                        status:
                          e.target
                            .value,
                      })
                    )
                  }
                >
                  <option value="">
                    All
                  </option>

                  <option value="SUCCESS">
                    Success
                  </option>

                  <option value="FAILED">
                    Failed
                  </option>

                  <option value="PENDING">
                    Pending
                  </option>

                  <option value="PROCESSING">
                    Processing
                  </option>

                  <option value="RETRYING">
                    Retrying
                  </option>
                </Input>
              </Col>

              <Col
                md={3}
                className="d-flex align-items-end gap-2"
              >
                <Button
                  color="primary"
                  onClick={() =>
                    loadLogs()
                  }
                  disabled={
                    loading
                  }
                >
                  {loading ? (
                    <>
                      <Spinner
                        size="sm"
                        className="me-1"
                      />
                      Loading
                    </>
                  ) : (
                    <>
                      <i className="bx bx-filter-alt me-1" />
                      Apply Filters
                    </>
                  )}
                </Button>

                <Button
                  color="light"
                  onClick={
                    clearFilters
                  }
                >
                  Clear
                </Button>
              </Col>

            </Row>
          </CardBody>
        </Card>

        {/* =====================================
            MAIN TABLE
        ===================================== */}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">

            <div>
              <h4 className="card-title mb-1">
                Channel Sync Activity
              </h4>

              <p className="text-muted mb-0">
                Inventory, rates,
                restrictions and
                reservation
                synchronization
              </p>
            </div>

            <Badge color="primary">
              {otaLogs.length} Records
            </Badge>

          </CardHeader>

          <CardBody>

            {loading ? (
              <div className="text-center py-5">
                <Spinner
                  color="primary"
                />

                <div className="mt-2 text-muted">
                  Loading sync logs...
                </div>
              </div>
            ) : (
              <TableContainer
                columns={
                  syncColumns
                }
                data={
                  otaLogs
                }
                customPageSize={
                  10
                }
                isPagination={
                  true
                }
                className="text-nowrap"
                theadClass="table-light"
              />
            )}

          </CardBody>
        </Card>

        {/* =====================================
            CHANNEL HEALTH
        ===================================== */}

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Channel Health
                </h4>
              </CardHeader>

              <CardBody>

                {channels.length ===
                0 ? (
                  <div className="text-center text-muted py-4">
                    No channels configured.
                  </div>
                ) : (
                  channels.map(
                    (channel) => {
                      const health =
                        getChannelStatus(
                          channel
                        );

                      return (
                        <div
                          className="mb-4"
                          key={
                            channel._id
                          }
                        >

                          <div className="d-flex justify-content-between align-items-center mb-1">

                            <strong>
                              {
                                channel.channel
                              }
                            </strong>

                            <Badge
                              color={
                                health.color
                              }
                            >
                              {
                                health.label
                              }
                            </Badge>

                          </div>

                          <div className="progress">
                            <div
                              className={`progress-bar bg-${health.color}`}
                              role="progressbar"
                              style={{
                                width:
                                  `${health.width}`,
                              }}
                            >
                              {
                                health.width
                              }
                            </div>
                          </div>

                        </div>
                      );
                    }
                  )
                )}

              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Sync Engine
                </h4>
              </CardHeader>

              <CardBody>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Total Syncs
                  </span>

                  <Badge color="primary">
                    {
                      dashboard.totalSyncs
                    }
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Successful
                  </span>

                  <Badge color="success">
                    {
                      dashboard.success
                    }
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Failed
                  </span>

                  <Badge color="danger">
                    {
                      dashboard.failed
                    }
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Pending
                  </span>

                  <Badge color="warning">
                    {
                      dashboard.pending
                    }
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Processing
                  </span>

                  <Badge color="info">
                    {
                      dashboard.processing
                    }
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    Retrying
                  </span>

                  <Badge color="secondary">
                    {
                      dashboard.retrying
                    }
                  </Badge>
                </div>

              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default SyncLogs;